import { useCallback } from "react";
import { Doc } from "sharedb/lib/client";

import { FieldDataType, WorksheetType } from "../shared/types/worksheet";
import { BLOCKS_PATH, DATA_PATH } from "../shared/worksheets/constants";
import { hasEditableBlocks } from "../shared/worksheets/doc-helpers";

import { ReleaseStatus, isArchived } from "./activity";
import { rollbarAndLogError } from "./logger";
import { maybeConfirmUnpublishOnMove } from "./page-move";
import { presentSession, usePresentation } from "./presentation";
import type { ModelType } from "./type-utils";
import { shouldAddOverlayParam } from "./urls";
import { blankInstructorNotesWorksheetData } from "./worksheet";

import { serverCopyPage, serverMovePageWithinSection } from "api/api-server-actions";
import {
  ACCESS_ID_QUERY_PARAM,
  FOLLOWING_QUERY_PARAM,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
  SecondaryOverlay,
  SecondaryOverlayType,
} from "components/constants";
import {
  ActivityPageType,
  ActivityType,
  InteractionMode,
  PageCategory,
  PageType,
  SectionPageCategory,
  coursePageCategories,
} from "components/server-types";
import { toastReleasePage, toastUnreleasePage } from "helpers/toasts";
import { t } from "i18n/i18n";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { EXAMPLE_CONTENT_BLOCKS, EXAMPLE_CONTENT_DATA } from "seeds/worksheets/example-blocks";
import { useCapabilities, usePageCapabilitiesForPageId } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  ActivityWithPagesType,
  selectCurrentSectionId,
  selectCurrentUserId,
  selectIsPracticeClassSession,
  selectScreensharingClassUserId,
  selectSelectedPageTabVariant,
} from "store/selectors";
import {
  toastCopyOperationCatcher,
  toastLocalizedOperationCatcher,
  toastMoveOperationCatcher,
  toastSuccessMessage,
} from "utils/alerts";
import {
  isGroupInteractionMode,
  isSharedClassInteractionMode,
  isSharedClassPage,
  isStaticPage,
} from "utils/page-utils";
import { getQueryParamFromStore, useNavigate, useQueryParamsActions } from "utils/routing";
import { refreshTopicPageCounts, topicIdForPage } from "utils/topic";

export const PAGE_LIST_CUTOFF_LENGTH = 10;
// The default number of page groups to create when a page is first switched into a group
// interaction mode (e.g. ASSIGN_STUDENTS / SELF_ASSIGN).
const DEFAULT_INITIAL_PAGE_GROUP_COUNT = 2;

export const pageTitle = (pageCategory: PageCategory) => {
  return t(`common.default.${pageCategory}`);
};

export const isAssignment = (pageCategory: PageCategory) => {
  return pageCategory === "assignment";
};

export const isSectionPageCategory = (pageCategory: PageCategory) => {
  return coursePageCategories.includes(pageCategory as SectionPageCategory);
};

export const isSectionPage = (page: Pick<PageType, "category"> | null) => {
  return !!page && isSectionPageCategory(page.category);
};

export const sumActivityTimes = (activityList: ActivityType[]) => {
  return activityList.reduce((total, activity) => total + Number(activity.estimated_time), 0);
};

export const getReleaseStatus = (pages: PageType[]): ReleaseStatus =>
  pages.length === 0
    ? null
    : pages.every((page) => page.released_at)
      ? "released"
      : pages.some((page) => page.released_at)
        ? "partiallyReleased"
        : "unreleased";

// Releases a set of pages for a topic or activity.
// TODO: Eventually we'd like a way to make bulk updates via storeApi
export const changeReleaseStatusForPages = async (pages: PageType[]) => {
  let pagesToUpdate = pages.filter((page) => !page.released_at);
  const shouldReleasePages = pagesToUpdate.length > 0;
  if (!shouldReleasePages) {
    pagesToUpdate = pages;
  }

  const length = pagesToUpdate.length;

  try {
    await Promise.all(
      pagesToUpdate.map((page) =>
        storeApi.pages.partial_update({
          id: page.id,
          released_at: shouldReleasePages ? new Date().toJSON() : null,
        }),
      ),
    );

    await refreshTopicPageCounts(topicIdForPage(pagesToUpdate[0]));

    if (shouldReleasePages) {
      toastReleasePage(length);
    } else {
      toastUnreleasePage(length);
    }
  } catch (e) {
    toastLocalizedOperationCatcher("update_pages")(e);
  }
};

/**
 * Updates a page's `interaction_mode` and reconciles related page-group state:
 *  - When switching into a group mode in a non-template section, ensures there are
 *    `DEFAULT_INITIAL_PAGE_GROUP_COUNT` page groups (creates them if missing).
 *  - When switching out of a group mode in a non-template section, destroys all
 *    existing page groups for the page.
 *  - When switching into a shared-class mode, zeroes `max_points` (shared pages
 *    don't track per-student points).
 *
 * Page groups are fetched lazily on demand — only when the transition crosses
 * the group boundary — so callers don't need to pre-load them just in case.
 *
 * Does not handle UI side effects (e.g. toggling a "show group config" panel) —
 * callers should layer that on top.
 */
export const changePageInteractionMode = async ({
  page,
  newInteractionMode,
  isInTemplateSection,
}: {
  page: Pick<PageType, "id" | "interaction_mode" | "section_id">;
  newInteractionMode: InteractionMode;
  isInTemplateSection: boolean;
}) => {
  if (newInteractionMode === page.interaction_mode) {
    return;
  }

  const wasGrouped = isGroupInteractionMode(page.interaction_mode);
  const isNowGrouped = isGroupInteractionMode(newInteractionMode);
  const isNowShared = isSharedClassInteractionMode(newInteractionMode);

  if (!isInTemplateSection && wasGrouped !== isNowGrouped) {
    const pageGroups = await storeApi.page_groups.list({ page_id: page.id });

    if (isNowGrouped && pageGroups.length === 0) {
      Array.from({ length: DEFAULT_INITIAL_PAGE_GROUP_COUNT }).map(() =>
        storeApi.page_groups.create({
          page_id: page.id,
          section_id: page.section_id,
        }),
      );
    } else if (!isNowGrouped && pageGroups.length > 0) {
      pageGroups.map((pg) => storeApi.page_groups.destroy(pg.id));
    }
  }

  const pageValues: Partial<PageType> & ModelType = {
    id: page.id,
    interaction_mode: newInteractionMode,
  };

  if (isNowShared) {
    pageValues.max_points = 0;
  }

  await storeApi.pages.partial_update(pageValues);
};

/*
 * The following hook returns functions for maintaining pages in the store.
 */
export const usePageActions = () => {
  const { canAuthorSection, canParticipateInSection } = useCapabilities();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const userId = useAppSelector(selectCurrentUserId);
  const screensharingClassUserId = useAppSelector(selectScreensharingClassUserId);
  const isSmallScreen = !useIsMdOrLarger();

  const navigate = useNavigate();
  const { setQueryParams } = useQueryParamsActions();

  const {
    presentedSessionId,
    hasStudentLeftPresentation,
    isPresenting,
    isFollowing,
    presentedPageId,
  } = usePresentation();
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);
  const isStudent = canParticipateInSection;

  const savePage = useCallback(async (page: Partial<PageType> & ModelType) => {
    const result = await storeApi.pages.partial_update(page);
    if ("released_at" in page) {
      await refreshTopicPageCounts(topicIdForPage(page));
    }

    return result;
  }, []);

  const dropPage = useCallback(
    async (droppedPage: ActivityPageType, activities: ActivityWithPagesType[]) => {
      if (!activities) {
        savePage({ id: droppedPage.id, order: droppedPage.order });
        return;
      }

      const targetActivity = activities.find((a) => a.id === droppedPage.activity_id);
      if (!targetActivity) {
        // We expect to find an activity, but it wasn't passed to us, this is probably an actual error
        const activityIds = activities.map((a) => a.id);
        rollbarAndLogError(`Dragging page failed due to missing target activity`, {
          pageId: droppedPage.id,
          activityIds,
        });
        return;
      }

      // Dragging a page to the archive should unrelease it
      if (isArchived(targetActivity.category)) {
        const confirmMove = await maybeConfirmUnpublishOnMove(droppedPage, {
          activityCategory: targetActivity.category,
        });

        if (confirmMove) {
          // This also unpublishes the page
          savePage({
            id: droppedPage.id,
            released_at: null,
            assessments_published_at: null,
            activity_id: droppedPage.activity_id,
            order: droppedPage.order,
          });
        } else {
          // This forces a re-fresh, getting the server-side data and re-rendering the sidebar
          savePage({ id: droppedPage.id });
        }
      } else {
        savePage({ id: droppedPage.id, activity_id: targetActivity.id, order: droppedPage.order });
      }
    },
    [savePage],
  );

  const selectPage = useCallback(
    (page: Pick<PageType, "id" | "released_at"> | null, pageUrl?: URL) => {
      // 1. We need to update the session to present the new page
      if (isPresenting && presentedSessionId && page && page.id !== presentedPageId) {
        presentSession(presentedSessionId, {
          page,
          userId: userId || null,
          isPractice: isPracticeClassSession || null,
          accessId: null,
          isRollup: false,
        });
      }

      // 2. We need to update the url params to reflect the new page

      // Set the isFollowing parameter based on our class session state
      // This shouldn't happen, but an instructor could type it into a url.
      const instructorIsFollowing = isPresenting && isFollowing;
      // If the user is following but is an instructor, we can delete the
      // follow param when they select a page that isn't being presented.
      const instructorNavigatedAway =
        isFollowing && canAuthorSection && page?.id !== presentedPageId;

      if (pageUrl) {
        const urlParams = pageUrl.searchParams;
        urlParams.delete(ACCESS_ID_QUERY_PARAM);
        urlParams.delete(SUBMISSION_ID_QUERY_PARAM);

        if (urlParams.get(SECONDARY_OVERLAY_QUERY_PARAM) === "outcomes") {
          urlParams.delete(SECONDARY_OVERLAY_QUERY_PARAM);
        }

        if (!screensharingClassUserId && (instructorIsFollowing || instructorNavigatedAway)) {
          urlParams.delete(FOLLOWING_QUERY_PARAM);
        } else if (isStudent && isFollowing && page?.id !== presentedPageId) {
          urlParams.set(FOLLOWING_QUERY_PARAM, "passive");
        } else if (isStudent && hasStudentLeftPresentation && page?.id === presentedPageId) {
          urlParams.set(FOLLOWING_QUERY_PARAM, "active");
        }
      } else {
        const paramsToUpdate = { [ACCESS_ID_QUERY_PARAM]: null, [SUBMISSION_ID_QUERY_PARAM]: null };

        if (getQueryParamFromStore(SECONDARY_OVERLAY_QUERY_PARAM) === SecondaryOverlay.Outcomes) {
          paramsToUpdate[SECONDARY_OVERLAY_QUERY_PARAM] = null;
        }

        if (!screensharingClassUserId && (instructorIsFollowing || instructorNavigatedAway)) {
          paramsToUpdate[FOLLOWING_QUERY_PARAM] = null;
        } else if (isStudent && isFollowing && page?.id !== presentedPageId) {
          paramsToUpdate[FOLLOWING_QUERY_PARAM] = "passive";
        } else if (isStudent && hasStudentLeftPresentation && page?.id === presentedPageId) {
          paramsToUpdate[FOLLOWING_QUERY_PARAM] = "active";
        }

        setQueryParams(paramsToUpdate);
      }
    },
    [
      isPresenting,
      isFollowing,
      isStudent,
      hasStudentLeftPresentation,
      canAuthorSection,
      presentedPageId,
      presentedSessionId,
      userId,
      isPracticeClassSession,
      screensharingClassUserId,
      setQueryParams,
    ],
  );

  // Normally when we call select page we are just updating the existing url, but it's possible
  // we are switching pages entirely, so we need to set any parameters it would set on our new url
  // not on the existing search pages.
  const navigateToPage = useCallback(
    (page: Pick<PageType, "id" | "released_at"> | null, pageUrl: string) => {
      if (!page) {
        return;
      }
      const newUrl = new URL(pageUrl, window.location.origin);

      if (!newUrl.searchParams.has(SECONDARY_OVERLAY_QUERY_PARAM)) {
        const overlay = getQueryParamFromStore(SECONDARY_OVERLAY_QUERY_PARAM);
        if (overlay && shouldAddOverlayParam(overlay as SecondaryOverlayType, isSmallScreen)) {
          newUrl.searchParams.set(SECONDARY_OVERLAY_QUERY_PARAM, overlay);
        }
      }

      selectPage(page, newUrl);
      navigate(newUrl.pathname + newUrl.search);
    },
    [isSmallScreen, navigate, selectPage],
  );

  const createPage = useCallback(
    async (category: PageCategory, activity_id?: string) => {
      const isSeedingNewWorksheets = getQueryParamFromStore("seedWorksheets") === "1";
      const initial_worksheet_data = isSeedingNewWorksheets
        ? ({
            [BLOCKS_PATH]: EXAMPLE_CONTENT_BLOCKS,
            [DATA_PATH]: EXAMPLE_CONTENT_DATA as unknown as FieldDataType, // TODO: Make example data match FieldDataType
          } as Partial<WorksheetType>)
        : {};

      const page = await storeApi.pages.create({
        category,
        title: pageTitle(category),
        section_id: sectionId!,
        activity_id,
        initial_worksheet_data,
        interaction_mode: InteractionMode.STATIC,
        instructor_notes_worksheet_data: blankInstructorNotesWorksheetData(),
      });

      return page;
    },
    [sectionId],
  );

  const deletePage = useCallback(
    (page: Pick<PageType, "id">) => storeApi.pages.destroy(page.id),
    [],
  );

  const lockPage = useCallback(
    (page: Pick<PageType, "id">) => {
      savePage({ id: page.id, locked_at: new Date().toISOString() });
    },
    [savePage],
  );

  const unlockPage = useCallback(
    (page: Pick<PageType, "id">) => {
      savePage({ id: page.id, locked_at: null });
    },
    [savePage],
  );

  const copyPage = useCallback(
    (
      category: PageCategory,
      itemId: string,
      { sectionIds, activityIds }: { sectionIds?: string[]; activityIds?: string[] },
    ) => {
      serverCopyPage(itemId, sectionIds, activityIds)
        .then(() => {
          const i18nCategory = category;
          toastSuccessMessage(
            t("content_view.item_copied", { item: t(`glossary.${i18nCategory}`) }),
          );
        })
        .catch(toastCopyOperationCatcher(category));
    },
    [],
  );

  const movePageWithinSection = useCallback(
    async (
      page: Pick<PageType, "id" | "assessments_published_at">,
      {
        destinationCategory,
        destinationTopicId,
      }: { destinationCategory: PageCategory; destinationTopicId?: string },
    ) => {
      const confirmMove = await maybeConfirmUnpublishOnMove(page, {
        pageCategory: destinationCategory,
      });

      if (!confirmMove) {
        return;
      }

      // When a page is moved to the "activity_page" category, from the user's perspective it's moved
      // to another Topic. Better handling later would be to also show the Topic's name in the toast.
      const destinationKey =
        destinationCategory === "activity_page" ? "topic" : destinationCategory;

      serverMovePageWithinSection(page.id, destinationCategory, destinationTopicId)
        .then(() => {
          toastSuccessMessage(
            t("content_view.item_moved", {
              destinationCategory: t(`glossary.${destinationKey}`),
            }),
          );
        })
        .catch(toastMoveOperationCatcher(destinationKey));
    },
    [],
  );

  return {
    createPage,
    savePage,
    deletePage,
    lockPage,
    unlockPage,
    copyPage,
    dropPage,
    selectPage,
    navigateToPage,
    movePageWithinSection,
  };
};

export const useShouldShowStudentSubmitButton = (
  page: Pick<PageType, "id" | "due_at" | "interaction_mode"> | null,
  isDocLoaded: boolean,
) => {
  const selectedPageTabVariant = useAppSelector(selectSelectedPageTabVariant);
  const { canMakeResponse } = usePageCapabilitiesForPageId(page?.id || null);

  const isWorkspaceOrSubmissionPage =
    selectedPageTabVariant === "my_draft" || selectedPageTabVariant === "my_submission";
  const pageIsStatic = !!page && isStaticPage(page);
  const isSharedPage = !!page && isSharedClassPage(page);
  const isPlannerEligible = !pageIsStatic || page?.due_at;

  return (
    isPlannerEligible &&
    !isSharedPage &&
    canMakeResponse &&
    isWorkspaceOrSubmissionPage &&
    isDocLoaded
  );
};

// If a static page gets a response block added, the system always makes it an "Individual" interaction mode
export const updatePageStaticContentStatus = (
  page: Pick<PageType, "id" | "interaction_mode">,
  doc: Doc<WorksheetType>,
) => {
  const isStaticContent = !hasEditableBlocks(doc);
  const pageIsStatic = isStaticPage(page);
  if (isStaticContent && !pageIsStatic) {
    storeApi.pages.partial_update({ id: page.id, interaction_mode: InteractionMode.STATIC });
  } else if (!isStaticContent && pageIsStatic) {
    storeApi.pages.partial_update({ id: page.id, interaction_mode: InteractionMode.INDIVIDUAL });
  }
};

export const markPageAssessmentsStatus = async (pageId: string, complete: boolean) => {
  await storeApi.pages.partial_update({
    id: pageId,
    assessing_marked_complete_at: complete ? new Date().toISOString() : null,
  });
};
